import React from "react";
import { NavLink, useParams } from "react-router-dom";
import { activateUser } from "core/services/site/user";
import { cx } from "utils/join-classnames";
import { useFormValidation } from "ui/hooks/use-form-validation";
import { Button } from "ui/components";
import Spinner from "ui/components/atoms/Spinner";
import FormElement from "ui/components/molecules/FormElement";
import styles from "./Activate.module.scss";

const Activate: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [pending, setPending] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string>("");
    const [confirm, setConfirm] = React.useState<boolean>(false);

    const theForm = useFormValidation({
        password: {
            valid: false,
            touched: false,
            value: "",
            errorMsg: "",
            rules: {
                required: true,
                minLength: 8,
                maxLength: 20,
            },
        },
        passwordconfirm: {
            valid: false,
            touched: false,
            value: "",
            errorMsg: "",
            rules: {
                required: true,
                minLength: 8,
                maxLength: 20,
                isSameAsReference: "password",
            },
        },
    });

    const handleActivate = async () => {
        setPending(true);
        try {
            if (!theForm.formState.password.value) {
                throw Error("no password");
            }
            if (!token) {
                throw Error("invalid token");
            }
            const response = await activateUser(
                token,
                theForm.formState.password.value.toString(),
            );
            if (response.error) {
                throw Error(response.error);
            } else {
                setConfirm(true);
            }
        } catch (err) {
            setError((err as Error).message);
        }
        setPending(false);
    };

    const tokenValid = token && token.length > 20 && token.length < 45;

    return (
        <div className={styles.page}>
            <div className={styles.activate}>
                <img
                    src="../static/img/logo-spoton.svg"
                    alt="SpotOn"
                    className={styles.activate__logo}
                />
                {!tokenValid && (
                    <div className={styles.activate__heading}>
                        Invalid token
                    </div>
                )}
                {confirm && (
                    <>
                        <div className={styles.activate__heading}>
                            Your account was activated!
                        </div>
                        <div className={styles.activate__text}>
                            Log in and start clipping
                        </div>
                        <NavLink to="/login">
                            <Button
                                type="primary"
                                theme="light"
                                disabled={pending}
                                className={cx(
                                    styles.activate__btn,
                                    styles.activateBtnLogin,
                                )}
                            >
                                Proceed to login
                            </Button>
                        </NavLink>
                    </>
                )}
                {tokenValid && !confirm && (
                    <>
                        <div className={styles.activate__heading}>
                            Activate your account
                        </div>
                        <div className={styles.activate__text}>
                            Choose a password and you will be clipping
                            livestreams before you know it.
                        </div>
                        {error !== "" && (
                            <div className={styles.errormsg}>{error}</div>
                        )}
                        <div className={styles.activate__form}>
                            {pending && (
                                <span className={styles.activate__spinner}>
                                    <Spinner />
                                </span>
                            )}
                            <div>
                                <FormElement
                                    title="Password"
                                    onChange={(value) => {
                                        theForm.inputChangeHandler(
                                            "password",
                                            value,
                                        );
                                    }}
                                    defaultText="Password"
                                    theme="dark"
                                    type="password"
                                    id="password"
                                    fieldState={theForm.formState.password}
                                />
                                <FormElement
                                    title="Confirm password"
                                    onChange={(value) => {
                                        theForm.inputChangeHandler(
                                            "passwordconfirm",
                                            value,
                                        );
                                    }}
                                    defaultText="Confirm password"
                                    theme="dark"
                                    type="password"
                                    id="passwordconfirm"
                                    fieldState={
                                        theForm.formState.passwordconfirm
                                    }
                                />
                                <Button
                                    type="primary"
                                    onClick={handleActivate}
                                    disabled={!theForm.formValid || pending}
                                    className={cx(
                                        styles.activate__btn,
                                        styles.activate__btn__primary,
                                    )}
                                >
                                    <span
                                        className={
                                            styles.activate__button__label
                                        }
                                    >
                                        Activate account
                                    </span>
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Activate;
